import React, { useState, useEffect } from 'react';
import { campaignAPI } from '../api';
import { Link } from 'react-router-dom';

function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [error, setError] = useState('');
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateCampaignId, setDuplicateCampaignId] = useState(null);
  const [duplicateName, setDuplicateName] = useState('');

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      const response = await campaignAPI.getAll();
      setCampaigns(response.data);
    } catch (error) {
      console.error('Error loading campaigns:', error);
      setError('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await campaignAPI.create(formData);
      setShowModal(false);
      setFormData({ name: '', description: '' });
      loadCampaigns();
    } catch (error) {
      console.error('Error creating campaign:', error);
      setError('Failed to create campaign');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this campaign?')) {
      try {
        await campaignAPI.delete(id);
        loadCampaigns();
      } catch (error) {
        console.error('Error deleting campaign:', error);
        setError('Failed to delete campaign');
      }
    }
  };

  const handleDuplicate = (id, campaignName) => {
    setDuplicateCampaignId(id);
    setDuplicateName(`${campaignName} (Copy)`);
    setShowDuplicateModal(true);
  };

  const handleDuplicateSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await campaignAPI.duplicate(duplicateCampaignId, duplicateName);
      setShowDuplicateModal(false);
      setDuplicateCampaignId(null);
      setDuplicateName('');
      alert(`Campaign duplicated! New campaign: "${response.data.name}"`);
      loadCampaigns();
    } catch (error) {
      console.error('Error duplicating campaign:', error);
      setError('Failed to duplicate campaign');
    }
  };

  if (loading) {
    return <div className="container"><div className="loading">Loading campaigns...</div></div>;
  }

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Campaigns</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          Create Campaign
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="card">
        {campaigns.length === 0 ? (
          <div className="empty-state">
            <h3>No campaigns yet</h3>
            <p>Create your first campaign to start sending WhatsApp messages!</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map(campaign => (
                  <tr key={campaign.id}>
                    <td>{campaign.name}</td>
                    <td>{campaign.description}</td>
                    <td>
                      <span className={`badge badge-${
                        campaign.status === 'draft' ? 'warning' : 
                        campaign.status === 'active' ? 'success' : 'info'
                      }`}>
                        {campaign.status}
                      </span>
                  </td>
                  <td>{new Date(campaign.created_at).toLocaleDateString()}</td>
                  <td>
                    <div className="button-group">
                      <Link to={`/campaigns/${campaign.id}`}>
                        <button className="btn btn-primary">Manage</button>
                      </Link>
                      <button className="btn btn-secondary" onClick={() => handleDuplicate(campaign.id, campaign.name)}>
                        Duplicate
                      </button>
                      <button className="btn btn-danger" onClick={() => handleDelete(campaign.id)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Create New Campaign</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Campaign Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter campaign name"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter campaign description"
                />
              </div>
              <div className="button-group">
                <button type="submit" className="btn btn-primary">Create</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDuplicateModal && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Duplicate Campaign</h2>
              <button className="close-btn" onClick={() => setShowDuplicateModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleDuplicateSubmit}>
              <div className="form-group">
                <label>New Campaign Name *</label>
                <input
                  type="text"
                  required
                  value={duplicateName}
                  onChange={(e) => setDuplicateName(e.target.value)}
                  placeholder="Enter new campaign name"
                  autoFocus
                />
              </div>
              <p style={{ color: '#666', fontSize: '0.9em', marginTop: '10px' }}>
                This will copy all messages, settings, and configuration. No contacts will be included.
              </p>
              <div className="button-group">
                <button type="submit" className="btn btn-primary">Duplicate</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowDuplicateModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Campaigns;
